import type { LandingPagePartner, LandingPagePlansData } from '@provision/landing-page-ui';
import type { LandingPageConfig } from '@provision/landing-page-utils';

/**
 * The preview tab reads its payload here rather than from the URL, because the config is partner
 * marketing copy. The key is scoped per brand so a tab opened for one brand can never pick up
 * another's, and entries carry a timestamp so one left behind by a crash expires on its own.
 */
const KEY_PREFIX = 'pt-landing-page-preview:';

/** Only has to outlive an editing session; anything older is a leftover, not a live preview. */
const MAX_AGE_MS = 60 * 60 * 1000;

export interface LandingPagePreviewPayload {
  config: LandingPageConfig;
  plans: LandingPagePlansData;
  partner?: LandingPagePartner;
  logoUrl?: string;
}

interface StoredEntry {
  payload: LandingPagePreviewPayload;
  savedAt: number;
}

const keyFor = (brandId: string): string => `${KEY_PREFIX}${brandId}`;

function parseEntry(raw: string | null): StoredEntry | null {
  if (!raw) return null;

  try {
    const entry = JSON.parse(raw) as StoredEntry;

    return typeof entry.savedAt === 'number' ? entry : null;
  } catch {
    return null;
  }
}

const isExpired = (entry: StoredEntry): boolean => Date.now() - entry.savedAt > MAX_AGE_MS;

/** Drops every expired preview, including brands this tab is not looking at. */
function sweepExpired(): void {
  for (const key of Object.keys(localStorage)) {
    if (!key.startsWith(KEY_PREFIX)) continue;

    const entry = parseEntry(localStorage.getItem(key));
    if (!entry || isExpired(entry)) localStorage.removeItem(key);
  }
}

export function writeLandingPagePreview(
  brandId: string,
  payload: LandingPagePreviewPayload,
): void {
  try {
    localStorage.setItem(keyFor(brandId), JSON.stringify({ payload, savedAt: Date.now() }));
  } catch {
    // A blocked store only costs the second window.
  }
}

export function clearLandingPagePreview(brandId: string): void {
  try {
    localStorage.removeItem(keyFor(brandId));
  } catch {
    // Nothing to do; the entry expires on its own.
  }
}

export function readLandingPagePreview(brandId: string): LandingPagePreviewPayload | null {
  try {
    sweepExpired();

    return parseEntry(localStorage.getItem(keyFor(brandId)))?.payload ?? null;
  } catch {
    return null;
  }
}

/** Calls back whenever the editor writes or clears this brand's preview. Returns an unsubscribe. */
export function subscribeToLandingPagePreview(
  brandId: string,
  onChange: (payload: LandingPagePreviewPayload | null) => void,
): () => void {
  const key = keyFor(brandId);

  const handle = (event: StorageEvent) => {
    if (event.key !== key) return;

    const entry = parseEntry(event.newValue);
    onChange(entry && !isExpired(entry) ? entry.payload : null);
  };

  window.addEventListener('storage', handle);

  return () => {
    window.removeEventListener('storage', handle);
  };
}
